import { FC, useEffect, useRef, useState } from 'react';
import { signalFailingImageUrl } from '../../api/products/signalFailingImg';

interface ProductButtonProps {
	productLabel: string;
	productImages: string[];
	width: number;
	height: number;
	isSelected: boolean;
	//onClick: () => void;
	onSelect: () => void;
}

const ProductButton: FC<ProductButtonProps> = ({
	productLabel,
	productImages,
	width,
	height,
	//onClick,
	onSelect,
	isSelected,
}) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isHovered, setIsHovered] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);

	const isDragging = useRef(false);
	const startX = useRef(0);
	const scrollLeft = useRef(0);

	const scrollEndTimer = useRef<NodeJS.Timeout | null>(null);

	const handleScroll = () => {
	if (!scrollRef.current) return;

	const scrollLeft = scrollRef.current.scrollLeft;
	const index = Math.round(scrollLeft / width);
	setCurrentIndex(index);

	// Clear previous timer
	if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);

	// Set a new timer to trigger after scroll ends
	scrollEndTimer.current = setTimeout(() => {
		if (scrollRef.current) {
			const containerWidth = scrollRef.current.offsetWidth;
			const scrollTo = width * index - (containerWidth - width) / 2;
			scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
		}
	}, 100); // wait 150ms after last scroll event
};

	useEffect(() => {
		const scrollEl = scrollRef.current;
		if (!scrollEl) return;

		const handleMouseDown = (e: MouseEvent) => {
			isDragging.current = true;
			startX.current = e.pageX - scrollEl.offsetLeft;
			scrollLeft.current = scrollEl.scrollLeft;
			scrollEl.style.cursor = 'grabbing';
		};

		const handleMouseLeave = () => {
			isDragging.current = false;
			scrollEl.style.cursor = 'default';
		};

		const handleMouseUp = () => {
			isDragging.current = false;
			scrollEl.style.cursor = 'default';
		};

		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging.current) return;
			e.preventDefault();
			const x = e.pageX - scrollEl.offsetLeft;
			const walk = (x - startX.current) * 1.5; // scroll-fastness factor
			scrollEl.scrollLeft = scrollLeft.current - walk;
		};

		scrollEl.addEventListener('mousedown', handleMouseDown);
		scrollEl.addEventListener('mouseleave', handleMouseLeave);
		scrollEl.addEventListener('mouseup', handleMouseUp);
		scrollEl.addEventListener('mousemove', handleMouseMove);

		return () => {
			scrollEl.removeEventListener('mousedown', handleMouseDown);
			scrollEl.removeEventListener('mouseleave', handleMouseLeave);
			scrollEl.removeEventListener('mouseup', handleMouseUp);
			scrollEl.removeEventListener('mousemove', handleMouseMove);
		};
	}, []);

		useEffect(() => {
			if (scrollRef.current && productImages.length > 0) {
				const scrollContainer = scrollRef.current;
				const containerWidth = scrollContainer.offsetWidth;

				// scroll to current image centered
				const scrollTo = width * currentIndex - (containerWidth - width) / 2;
				scrollContainer.scrollTo({ left: scrollTo, behavior: 'smooth' });
			}
		}, [currentIndex, width, productImages.length]);

	const handleClick = () => {
		//setIsSelected(true);
		onSelect();
	};

	const styles: { [key: string]: React.CSSProperties } = {
		tile: {
		width: 'clamp(4rem, 15vw, 8rem)',
		// height: 'clamp(4rem, 20vw, 9rem)',
		backgroundColor: isSelected || isHovered
			? 'rgb(var(--pop-secondary-20) / 1)' // dynamic color
			: 'white',  // fallback or white
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		borderRadius: 'clamp(0.5rem, 1.5vw, 0.75rem)',
		border: `1px solid ${isSelected || isHovered
			? 'rgb(var(--pop-secondary-90) / 1)'
			: 'rgb(var(--pop-neutral-10) / 1)'}`,
		margin: 12,
		overflow: 'hidden',
		cursor: 'pointer',
		boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
		transition: 'all 0.2s ease-in-out',
	},
		imageContainer: {
			width: '100%',
			height: height * 0.6,
			overflow: 'hidden',
			position: 'relative',
		},
		/*scrollWrapper: {
			display: 'flex',
			flexDirection: 'row',
			width: '100%',
			height: '100%',
			overflowX: 'scroll',
			scrollSnapType: 'both',
			scrollBehavior: '-moz-initial',
			msOverflowStyle: 'none',
			scrollbarWidth: 'none',
		},*/
		scrollWrapper: {
			display: 'flex',
			flexDirection: 'row',
			width: '100%',
			height: '100%',
			overflowX: 'scroll',
			//scrollSnapType: 'both',
			scrollSnapType: 'x mandatory',
			msOverflowStyle: 'none',
			scrollbarWidth: 'none',
			cursor: 'grab',
			scrollBehavior: 'smooth',
		},
		imageWrapper: {
			flexShrink: 0,
			width,
			height: '100%',
			scrollSnapAlign: 'center',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
		},
		image: {
			width: '100%',
			height: '100%',
			objectFit: 'cover',
			//objectPosition: 'top',
			borderRadius: 0,
		},
		dotsContainer: {
			position: 'absolute',
			bottom: 8,
			left: 0,
			right: 0,
			display: 'flex',
			justifyContent: 'center',
			gap: 6,
		},
		dot: {
			width: 'clamp(0.25rem, 1vw, 0.5rem)',
  			height: 'clamp(0.25rem, 1vw, 0.5rem)',
			borderRadius: 4,
			backgroundColor: 'rgb(var(--pop-primary-20) / var(--tw-border-opacity, 1))',
		},
		activeDot: {
			backgroundColor: 'rgb(var(--pop-secondary-90) / var(--tw-border-opacity, 1))',

		},
		text: {
			//marginTop: 10,
			padding: '0 12px',
			fontSize: 'clamp(0.5rem, 1.5vw, 0.75rem)',
			color: '#001234',
			textAlign: 'center',
			fontWeight: 300,
			transition: 'color 0.2s ease',
			display: '-webkit-box',
			WebkitLineClamp: 3,
			WebkitBoxOrient: 'vertical',
			overflow: 'hidden',
			width: '100%',
		},
	};

	return (
		<div
			style={styles.tile}
			onClick={handleClick}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div style={styles.imageContainer}>
				<div style={styles.scrollWrapper} ref={scrollRef} onScroll={handleScroll}>
					{productImages.map((img, i) => (
						<div style={styles.imageWrapper} key={i}>
							<img src={img} alt={productLabel} style={styles.image} draggable={false} onError={() => signalFailingImageUrl(img)} />
						</div>
					))}
				</div>
				<div style={styles.dotsContainer}>
					{productImages.map((_, i) => (
						<div
							key={i}
							style={{
								...styles.dot,
								...(i === currentIndex ? styles.activeDot : {}),
							}}
						/>
					))}
				</div>
			</div>
			<div style={styles.text}>{productLabel}</div>

		</div>
	);
};

export default ProductButton;